import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Loader2, BarChart3 } from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import SectionNav from '../../../components/layout/SectionNav';
import PageHeader from '../../../components/layout/PageHeader';
import { portfolioTabs } from '../../../nav/publicNav';

const PERIODS = ['1W', '1M', '3M', '6M', '1Y', 'ALL'];

function MetricCard({ label, value, sub, colorClass }) {
  return (
    <div className="text-center px-4 py-3">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-lg font-bold ${colorClass || 'text-foreground'}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function PerformancePage() {
  const performance = usePortfolioStore((s) => s.performance);
  const performanceLoading = usePortfolioStore((s) => s.performanceLoading);
  const fetchPerformance = usePortfolioStore((s) => s.fetchPerformance);
  const overview = usePortfolioStore((s) => s.overview);
  const fetchOverview = usePortfolioStore((s) => s.fetchOverview);
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);

  const [period, setPeriod] = useState('1M');
  const [showBenchmark, setShowBenchmark] = useState(true);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
    if (!overview) fetchOverview();
  }, [fetchHoldings, fetchOverview, holdings.length, overview]);

  useEffect(() => {
    fetchPerformance(period);
  }, [period, fetchPerformance]);

  const formatCurrency = (v) => {
    if (v == null) return '₹0';
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const p = performance;
  const ts = p?.timeseries || [];
  const hasBench = ts.length > 0 && ts[0]?.benchmark != null;

  const allHoldings = overview?.holdings || [];
  const sortedByPnl = [...allHoldings].sort((a, b) => (b.unrealizedPnL || 0) - (a.unrealizedPnL || 0));
  const topGainers = sortedByPnl.filter((h) => (h.unrealizedPnL || 0) > 0).slice(0, 5);
  const topLosers = sortedByPnl.filter((h) => (h.unrealizedPnL || 0) < 0).reverse().slice(0, 5);

  return (
    <div className="space-y-4">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader title="Performance" description="Track your portfolio returns over time" />

      {/* Period Selector */}
      <div className="flex items-center gap-2">
        {PERIODS.map((pr) => (
          <Button
            key={pr}
            size="sm"
            variant={period === pr ? 'default' : 'outline'}
            className={`text-xs h-7 px-3 ${period === pr ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            onClick={() => setPeriod(pr)}
          >
            {pr}
          </Button>
        ))}
        {hasBench && (
          <Button
            size="sm"
            variant={showBenchmark ? 'default' : 'outline'}
            className={`text-xs h-7 px-3 ml-auto ${showBenchmark ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
            onClick={() => setShowBenchmark(!showBenchmark)}
          >
            NIFTY 50
          </Button>
        )}
      </div>

      {performanceLoading && !p ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : !p || ts.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="py-16 text-center">
            <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Performance data not yet available.</p>
            <p className="text-xs text-muted-foreground mt-1">Historical snapshots are being built. Check back shortly.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Metrics */}
          <Card className="border-border/50">
            <CardContent className="p-0">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border/50">
                <MetricCard
                  label={`${period} Return`}
                  value={`${(p.percentReturn || 0) >= 0 ? '+' : ''}${(p.percentReturn || 0).toFixed(2)}%`}
                  colorClass={(p.percentReturn || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}
                />
                <MetricCard
                  label="Absolute Return"
                  value={`${(p.absoluteReturn || 0) >= 0 ? '+' : ''}${formatCurrency(p.absoluteReturn)}`}
                  colorClass={(p.absoluteReturn || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}
                />
                <MetricCard label="Start Value" value={formatCurrency(p.startValue)} />
                <MetricCard label="Current Value" value={formatCurrency(p.endValue)} />
              </div>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card className="border-border/50">
            <CardContent className="pt-4">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={ts}>
                    <defs>
                      <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="benchGradP" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => { const parts = d.split('-'); return `${parts[1]}/${parts[2]}`; }}
                      tick={{ fontSize: 10 }}
                      className="text-muted-foreground"
                    />
                    <YAxis
                      tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`}
                      tick={{ fontSize: 10 }}
                      className="text-muted-foreground"
                      width={65}
                    />
                    <Tooltip
                      formatter={(v, name) => [
                        `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                        name === 'value' ? 'Portfolio' : 'NIFTY 50 (scaled)'
                      ]}
                      labelFormatter={(l) => l}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="value" stroke="#10b981" fill="url(#perfGrad)" strokeWidth={2} name="Portfolio" dot={false} />
                    {showBenchmark && hasBench && (
                      <Area type="monotone" dataKey="benchmark" stroke="#6366f1" fill="url(#benchGradP)" strokeWidth={1.5} strokeDasharray="5 5" name="NIFTY 50 (scaled)" dot={false} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Attribution: Top Gainers + Losers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-emerald-500" /> Top Gainers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/30">
                  {topGainers.length > 0 ? topGainers.map((h) => (
                    <div key={h.symbol} className="flex items-center justify-between px-4 py-2">
                      <div>
                        <p className="text-sm font-medium">{h.company_name || h.symbol}</p>
                        <span className="text-[11px] text-muted-foreground">{h.symbol}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">+{formatCurrency(h.unrealizedPnL)}</p>
                        <p className="text-[11px] text-emerald-500">+{(h.unrealizedPnLPercent || 0).toFixed(1)}%</p>
                      </div>
                    </div>
                  )) : <p className="text-xs text-muted-foreground py-4 text-center">No gainers</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                  <TrendingDown className="w-4 h-4 text-red-500" /> Top Losers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/30">
                  {topLosers.length > 0 ? topLosers.map((h) => (
                    <div key={h.symbol} className="flex items-center justify-between px-4 py-2">
                      <div>
                        <p className="text-sm font-medium">{h.company_name || h.symbol}</p>
                        <span className="text-[11px] text-muted-foreground">{h.symbol}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-red-600">{formatCurrency(h.unrealizedPnL)}</p>
                        <p className="text-[11px] text-red-500">{(h.unrealizedPnLPercent || 0).toFixed(1)}%</p>
                      </div>
                    </div>
                  )) : <p className="text-xs text-muted-foreground py-4 text-center">No losers</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
